/**
 * SPJ-1731/1732: smistatore dei vincoli del merchandising plan per un negozio.
 *
 * Mostra per ogni tipo filato attuale e vincolo (pezzi e valore), chi sfora e chi ha margine,
 * con l'anteprima delle mosse in lista. Le mosse spostano pezzi e valore insieme (valore =
 * pezzi x prezzo della partenza) e si aggiungono a mano o con "Proponi ridistribuzione"
 * (backend, CP-SAT). Niente viene salvato finche' non si preme Conferma: allora le mosse
 * diventano spostamenti dei vincoli del negozio, che si salvano con il resto dell'MP.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from './Modal';

export type RebalanceRow = {
  tipoFilato: string;
  itemsActual: number;
  valueActual: number;
  itemsBound?: number;
  valueBound?: number;
  avgPriceBound?: number;
};

export type PriceSource = 'vincolo' | 'avp' | 'sku' | 'manca';

export type RebalanceMove = {
  id: string;
  from: string;
  to: string;
  items: number;
  value: number;
  price?: number;
  priceSource: PriceSource;
  /** prezzo non preso dal vincolo: il buyer lo deve confermare */
  priceConfirmed: boolean;
  origin: 'manuale' | 'proposta';
};

export type RebalanceProposalResponse = {
  status: 'optimal' | 'feasible' | 'nothing_to_do' | 'infeasible';
  moves: Array<{
    from_tipo_filato: string;
    to_tipo_filato: string;
    items: number;
    value: number;
    price?: number | null;
    price_source: PriceSource;
  }>;
  over_before: number;
  over_after: number;
};

const EPS = 0.5;
const PRICE_SOURCE_LABEL: Record<PriceSource, string> = {
  vincolo: 'prezzo dal vincolo',
  avp: 'prezzo AVP dell\'MP',
  sku: 'prezzo medio degli SKU',
  manca: 'senza prezzo: solo pezzi',
};

const has = (value: number | undefined): value is number => value !== undefined && value !== null && Number.isFinite(value);
const fmtItems = (value: number) => Math.round(value).toLocaleString('it-IT');
const fmtValue = (value: number) => `€ ${Math.round(value).toLocaleString('it-IT')}`;

/** Prezzo con cui i pezzi trascinano il valore (stessa catena del backend). */
export function resolvePrice(row: RebalanceRow): { price?: number; source: PriceSource } {
  if (has(row.itemsBound) && row.itemsBound > 0 && has(row.valueBound) && row.valueBound > 0) {
    return { price: row.valueBound / row.itemsBound, source: 'vincolo' };
  }
  if (has(row.avgPriceBound) && row.avgPriceBound > 0) return { price: row.avgPriceBound, source: 'avp' };
  if (row.itemsActual > 0 && row.valueActual > 0) return { price: row.valueActual / row.itemsActual, source: 'sku' };
  return { source: 'manca' };
}

/** Vincoli dopo le mosse (pezzi, valore) per tipo filato. */
export function applyMoves(rows: RebalanceRow[], moves: RebalanceMove[]) {
  const out = new Map(rows.map((r) => [r.tipoFilato, { items: r.itemsBound, value: r.valueBound }]));
  moves.forEach((mv) => {
    const src = out.get(mv.from);
    const dst = out.get(mv.to);
    if (!src || !dst) return;
    // una metrica si sposta solo se entrambi i lati hanno quel vincolo: cosi' il totale dell'MP
    // resta invariato
    if (mv.items && has(src.items) && has(dst.items)) {
      src.items -= mv.items;
      dst.items += mv.items;
    }
    if (mv.value && has(src.value) && has(dst.value)) {
      src.value -= mv.value;
      dst.value += mv.value;
    }
  });
  return out;
}

const overOf = (actual: number, bound?: number) => (has(bound) ? Math.max(0, actual - bound) : 0);
const isOver = (row: RebalanceRow, items?: number, value?: number) =>
  overOf(row.itemsActual, items) > EPS || overOf(row.valueActual, value) > EPS;

type TfOption = { tipoFilato: string; itemsGap?: number; valueGap?: number };

function gapLabel(o: TfOption): { text: string; tone: string } {
  const parts: string[] = [];
  const over = (o.itemsGap ?? 0) < -EPS || (o.valueGap ?? 0) < -EPS;
  if (has(o.itemsGap)) parts.push(`${fmtItems(Math.abs(o.itemsGap))} pz`);
  if (has(o.valueGap)) parts.push(fmtValue(Math.abs(o.valueGap)));
  if (parts.length === 0) return { text: 'senza vincolo', tone: 'text-gray-400' };
  if (over) return { text: `sfora ${parts.join(' · ')}`, tone: 'text-red-600' };
  const hasMargin = (o.itemsGap ?? 0) > EPS || (o.valueGap ?? 0) > EPS;
  return hasMargin ? { text: `margine ${parts.join(' · ')}`, tone: 'text-emerald-700' } : { text: 'al limite', tone: 'text-gray-400' };
}

/** Scelta del tipo filato cercabile, con margine/sforamento accanto a ogni voce. */
function TfPicker({
  label,
  value,
  onChange,
  options,
  hiddenCount,
  onShowHidden,
}: {
  label: string;
  value: string;
  onChange: (tf: string) => void;
  options: TfOption[];
  hiddenCount?: number;
  onShowHidden?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);
  const term = search.trim().toLowerCase();
  const filtered = term ? options.filter((o) => o.tipoFilato.toLowerCase().includes(term)) : options;
  const selected = options.find((o) => o.tipoFilato === value);
  const choose = (tf: string) => { onChange(tf); setOpen(false); setSearch(''); };
  return (
    <div ref={rootRef} className="relative col-span-2 text-xs text-gray-600">
      {label}
      <input
        type="text"
        role="combobox"
        aria-expanded={open}
        placeholder="Cerca tipo filato"
        value={open ? search : value}
        onFocus={() => setOpen(true)}
        onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
          if (e.key === 'Enter' && filtered.length > 0) { e.preventDefault(); choose(filtered[0].tipoFilato); }
        }}
        className="mt-0.5 w-full border border-gray-300 px-2 py-1 text-sm text-gray-900 focus:border-gray-900 focus:outline-none"
      />
      {!open && selected && (
        <span className={`pointer-events-none absolute right-2 top-[22px] text-[10px] font-semibold ${gapLabel(selected).tone}`}>{gapLabel(selected).text}</span>
      )}
      {open && (
        <ul role="listbox" className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto border border-gray-300 bg-white shadow-lg">
          {filtered.map((o) => {
            const g = gapLabel(o);
            return (
              <li
                key={o.tipoFilato}
                role="option"
                aria-selected={o.tipoFilato === value}
                onMouseDown={(e) => { e.preventDefault(); choose(o.tipoFilato); }}
                className={`flex cursor-pointer items-center justify-between gap-2 px-2 py-1.5 hover:bg-gray-50 ${o.tipoFilato === value ? 'bg-gray-100' : ''}`}
              >
                <span className="truncate font-semibold text-gray-900">{o.tipoFilato}</span>
                <span className={`shrink-0 text-[10px] font-semibold ${g.tone}`}>{g.text}</span>
              </li>
            );
          })}
          {filtered.length === 0 && <li className="px-2 py-2 italic text-gray-500">Nessun tipo filato</li>}
          {!!hiddenCount && !term && onShowHidden && (
            <li>
              <button type="button" onMouseDown={(e) => { e.preventDefault(); onShowHidden(); }} className="w-full px-2 py-1.5 text-left text-gray-500 underline hover:text-gray-900">
                mostra anche i {hiddenCount} senza margine
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

let moveSeq = 0;
const newId = () => `mv-${Date.now()}-${moveSeq++}`;

export function MpRebalancePanel({
  shopLabel,
  rows,
  initialFrom,
  onPropose,
  onConfirm,
  onClose,
}: {
  shopLabel: string;
  rows: RebalanceRow[];
  initialFrom?: string;
  onPropose: (rows: RebalanceRow[]) => Promise<RebalanceProposalResponse>;
  onConfirm: (moves: RebalanceMove[]) => void;
  onClose: () => void;
}) {
  const [moves, setMoves] = useState<RebalanceMove[]>([]);
  const [filter, setFilter] = useState<'tutti' | 'sforati' | 'margine'>('tutti');
  const [from, setFrom] = useState(initialFrom ?? '');
  const [to, setTo] = useState('');
  const [itemsDraft, setItemsDraft] = useState('');
  const [valueDraft, setValueDraft] = useState('');
  const [valueTouched, setValueTouched] = useState(false);
  const [proposing, setProposing] = useState(false);
  const [message, setMessage] = useState<{ tone: 'info' | 'error'; text: string } | null>(null);
  const [showAllFrom, setShowAllFrom] = useState(false);

  const byName = useMemo(() => new Map(rows.map((r) => [r.tipoFilato, r])), [rows]);
  const after = useMemo(() => applyMoves(rows, moves), [rows, moves]);
  // Tipi filato toccati dalle mosse in lista: chi cede e chi riceve.
  const touched = useMemo(() => {
    const out = new Map<string, { cede: boolean; riceve: boolean }>();
    const mark = (tf: string, key: 'cede' | 'riceve') => {
      out.set(tf, { ...(out.get(tf) ?? { cede: false, riceve: false }), [key]: true });
    };
    moves.forEach((m) => {
      mark(m.from, 'cede');
      mark(m.to, 'riceve');
    });
    return out;
  }, [moves]);
  const proposalCount = moves.filter((m) => m.origin === 'proposta').length;
  const overBefore = rows.filter((r) => isOver(r, r.itemsBound, r.valueBound)).length;
  const overAfter = rows.filter((r) => isOver(r, after.get(r.tipoFilato)?.items, after.get(r.tipoFilato)?.value)).length;

  const visibleRows = rows.filter((r) => {
    const a = after.get(r.tipoFilato);
    if (filter === 'sforati') return isOver(r, r.itemsBound, r.valueBound) || isOver(r, a?.items, a?.value);
    if (filter === 'margine') {
      return (has(a?.items) && a!.items! - r.itemsActual > EPS) || (has(a?.value) && a!.value! - r.valueActual > EPS);
    }
    return true;
  });

  // Opzioni dei campi Da / A, con margine o sforamento calcolati sui vincoli dopo le mosse in lista.
  const tfOptions: TfOption[] = rows.map((r) => {
    const a = after.get(r.tipoFilato);
    return {
      tipoFilato: r.tipoFilato,
      itemsGap: has(a?.items) ? a!.items! - r.itemsActual : undefined,
      valueGap: has(a?.value) ? a!.value! - r.valueActual : undefined,
    };
  });
  const hasMargin = (o: TfOption) => (o.itemsGap ?? 0) > EPS || (o.valueGap ?? 0) > EPS;
  const isOverOpt = (o: TfOption) => (o.itemsGap ?? 0) < -EPS || (o.valueGap ?? 0) < -EPS;
  // Da: prima chi ha piu' margine; chi non ha niente da cedere si vede solo a richiesta
  const fromCandidates = tfOptions
    .filter((o) => o.tipoFilato !== to && (showAllFrom || hasMargin(o) || o.tipoFilato === from))
    .sort((x, y) => (y.itemsGap ?? 0) - (x.itemsGap ?? 0) || (y.valueGap ?? 0) - (x.valueGap ?? 0));
  const hiddenFrom = tfOptions.filter((o) => o.tipoFilato !== to).length - fromCandidates.length;
  // A: prima chi sfora (di piu'), poi gli altri in ordine alfabetico
  const toCandidates = tfOptions
    .filter((o) => o.tipoFilato !== from)
    .sort((x, y) => {
      const ox = isOverOpt(x) ? 0 : 1;
      const oy = isOverOpt(y) ? 0 : 1;
      if (ox !== oy) return ox - oy;
      if (ox === 0) return (x.itemsGap ?? 0) - (y.itemsGap ?? 0) || (x.valueGap ?? 0) - (y.valueGap ?? 0);
      return x.tipoFilato.localeCompare(y.tipoFilato, 'it');
    });

  // Mossa manuale: i pezzi trascinano il valore al prezzo della partenza, se entrambi i lati hanno
  // il vincolo valore; tra tipi filato senza vincolo pezzi su entrambi i lati si sposta solo valore.
  const fromRow = byName.get(from);
  const toRow = byName.get(to);
  const bothItems = !!fromRow && !!toRow && has(fromRow.itemsBound) && has(toRow.itemsBound);
  const bothValue = !!fromRow && !!toRow && has(fromRow.valueBound) && has(toRow.valueBound);
  const draftPrice = fromRow ? resolvePrice(fromRow) : { source: 'manca' as PriceSource };
  const itemsNum = Math.max(0, Math.floor(Number(itemsDraft) || 0));
  const autoValue = bothValue && bothItems && draftPrice.price ? Math.round(itemsNum * draftPrice.price) : 0;
  const valueNum = valueTouched ? Math.max(0, Number(valueDraft) || 0) : autoValue;
  const canAdd = !!fromRow && !!toRow && from !== to && ((bothItems && itemsNum > 0) || (bothValue && valueNum > 0));

  const addManualMove = () => {
    if (!canAdd) return;
    const source: PriceSource = bothItems && bothValue ? draftPrice.source : 'manca';
    setMoves((prev) => [...prev, {
      id: newId(),
      from,
      to,
      items: bothItems ? itemsNum : 0,
      value: bothValue ? valueNum : 0,
      price: bothItems && bothValue ? draftPrice.price : undefined,
      priceSource: valueTouched ? 'manca' : source,
      // valore scritto a mano o prezzo non dal vincolo: va confermato
      priceConfirmed: !valueTouched && source === 'vincolo',
      origin: 'manuale',
    }]);
    setItemsDraft('');
    setValueDraft('');
    setValueTouched(false);
  };

  const propose = async () => {
    setProposing(true);
    setMessage(null);
    try {
      // la proposta parte dai vincoli gia' modificati dalle mosse manuali in lista
      const manual = moves.filter((m) => m.origin === 'manuale');
      const afterManual = applyMoves(rows, manual);
      const res = await onPropose(rows.map((r) => ({
        ...r,
        itemsBound: afterManual.get(r.tipoFilato)?.items,
        valueBound: afterManual.get(r.tipoFilato)?.value,
      })));
      if (res.status === 'nothing_to_do') {
        setMoves(manual);
        setMessage({ tone: 'info', text: 'Nessun tipo filato sfora: non serve ridistribuire.' });
        return;
      }
      if (res.status === 'infeasible') {
        setMessage({ tone: 'error', text: 'Non e\' stato possibile calcolare una proposta.' });
        return;
      }
      setMoves([...manual, ...res.moves.map((m) => ({
        id: newId(),
        from: m.from_tipo_filato,
        to: m.to_tipo_filato,
        items: m.items,
        value: m.value,
        price: m.price ?? undefined,
        priceSource: m.price_source,
        priceConfirmed: m.price_source === 'vincolo' || m.value === 0,
        origin: 'proposta' as const,
      }))]);
      setMessage(res.over_after > 0
        ? { tone: 'info', text: `Proposta: ${res.moves.length} mosse. Restano ${res.over_after} tipi filato sforati: il margine disponibile non basta a coprirli tutti.` }
        : { tone: 'info', text: `Proposta: ${res.moves.length} mosse, nessun tipo filato resta sforato.` });
    } catch (err) {
      setMessage({ tone: 'error', text: err instanceof Error ? err.message : 'Errore nella proposta di ridistribuzione.' });
    } finally {
      setProposing(false);
    }
  };

  const updateMove = (id: string, patch: Partial<RebalanceMove>) =>
    setMoves((prev) => prev.map((m) => (m.id === id ? { ...m, ...patch } : m)));
  const toConfirm = moves.filter((m) => !m.priceConfirmed).length;
  const negativeBound = [...after.values()].some((b) => (has(b.items) && b.items < 0) || (has(b.value) && b.value < -EPS));
  const invalidMove = negativeBound || moves.some((m) => m.items < 0 || m.value < 0 || (m.items === 0 && m.value === 0));

  const cell = (actual: number, bound: number | undefined, next: number | undefined, fmt: (n: number) => string) => {
    if (!has(bound)) return <span className="text-gray-300">—</span>;
    const changed = has(next) && Math.abs(next - bound) > 1e-6;
    const shown = changed ? next : bound;
    const gap = shown - actual;
    return (
      <div className="leading-tight">
        <div className="tabular-nums">
          {fmt(actual)} / {changed ? <><span className="text-gray-400 line-through">{fmt(bound)}</span> {fmt(next)}</> : fmt(bound)}
        </div>
        <div className={`text-[11px] font-semibold tabular-nums ${gap < -EPS ? 'text-red-600' : gap > EPS ? 'text-emerald-700' : 'text-gray-400'}`}>
          {gap < -EPS ? `sfora di ${fmt(-gap)}` : gap > EPS ? `margine ${fmt(gap)}` : 'al limite'}
        </div>
      </div>
    );
  };

  return (
    <Modal isOpen onClose={onClose}>
      {/* dimensione fissa: cambiando filtro o numero di mosse il pannello non si ridimensiona */}
      <div className="flex h-[88vh] w-[min(1100px,96vw)] flex-col">
        <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-5 py-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Smistatore · {shopLabel}</h3>
            <p className="text-sm text-gray-600">
              Sposta pezzi e valore tra tipi filato dello stesso negozio. Il totale del merchandising plan resta invariato.
            </p>
          </div>
          <div className="shrink-0 text-right text-sm">
            <div className="text-gray-500">Tipi filato che sforano</div>
            <div className="text-lg font-semibold tabular-nums">
              <span className={overBefore > 0 ? 'text-red-600' : 'text-gray-900'}>{overBefore}</span>
              <span className="mx-1 text-gray-400">→</span>
              <span className={overAfter > 0 ? 'text-red-600' : 'text-emerald-700'}>{overAfter}</span>
            </div>
          </div>
        </div>

        <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 overflow-hidden p-5 lg:grid-cols-[minmax(0,1fr)_380px]">
          {/* Tabella tipi filato con anteprima: scorre per conto suo */}
          <div className="flex min-h-0 min-w-0 flex-col">
            <div className="mb-2 flex items-center gap-2 text-xs">
              {(['tutti', 'sforati', 'margine'] as const).map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFilter(f)}
                  className={`border px-2 py-1 font-semibold ${filter === f ? 'border-black bg-black text-white' : 'border-gray-300 bg-white text-gray-700 hover:border-gray-900'}`}
                >
                  {f === 'tutti' ? 'Tutti' : f === 'sforati' ? 'Solo sforati' : 'Solo con margine'}
                </button>
              ))}
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto border border-gray-200">
              <table className="w-full table-fixed text-xs">
                <colgroup>
                  <col className="w-[30%]" />
                  <col className="w-[27%]" />
                  <col className="w-[30%]" />
                  <col className="w-[13%]" />
                </colgroup>
                <thead className="sticky top-0 bg-gray-50 text-left text-gray-500">
                  <tr>
                    <th className="px-2 py-2 font-medium">Tipo filato</th>
                    <th className="px-2 py-2 font-medium">Pezzi · attuale / vincolo</th>
                    <th className="px-2 py-2 font-medium">Valore · attuale / vincolo</th>
                    <th className="px-2 py-2 font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((r) => {
                    const a = after.get(r.tipoFilato);
                    const overNow = isOver(r, a?.items, a?.value);
                    return (
                      <tr
                        key={r.tipoFilato}
                        className={`border-t border-gray-100 ${overNow ? 'bg-red-50/60' : touched.has(r.tipoFilato) ? 'bg-amber-50' : ''}`}
                        style={touched.has(r.tipoFilato) ? { boxShadow: 'inset 3px 0 0 #d97706' } : undefined}
                      >
                        <td className="px-2 py-1.5" title={r.tipoFilato}>
                          <div className="truncate font-semibold text-gray-900">{r.tipoFilato}</div>
                          {touched.has(r.tipoFilato) && (
                            <div className="flex gap-1 text-[10px] font-semibold uppercase">
                              {touched.get(r.tipoFilato)!.cede && <span className="text-amber-700">cede</span>}
                              {touched.get(r.tipoFilato)!.riceve && <span className="text-sky-700">riceve</span>}
                            </div>
                          )}
                        </td>
                        <td className="px-2 py-1.5">{cell(r.itemsActual, r.itemsBound, a?.items, fmtItems)}</td>
                        <td className="px-2 py-1.5">{cell(r.valueActual, r.valueBound, a?.value, fmtValue)}</td>
                        <td className="whitespace-nowrap px-2 py-1.5 text-right">
                          <button type="button" className="mr-1 border border-gray-300 px-1.5 py-0.5 hover:border-gray-900" onClick={() => setFrom(r.tipoFilato)} title="Usa come partenza">da</button>
                          <button type="button" className="border border-gray-300 px-1.5 py-0.5 hover:border-gray-900" onClick={() => setTo(r.tipoFilato)} title="Usa come destinazione">a</button>
                        </td>
                      </tr>
                    );
                  })}
                  {visibleRows.length === 0 && (
                    <tr><td colSpan={4} className="px-2 py-4 text-center italic text-gray-500">Nessun tipo filato con questo filtro</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Spostamento manuale, proposta e mosse: scorrono per conto loro */}
          <div className="min-h-0 space-y-4 overflow-y-auto pr-1">
            <div className="space-y-2 border border-gray-200 p-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-600">Nuovo spostamento</div>
              <div className="grid grid-cols-2 gap-2">
                <TfPicker
                  label="Da"
                  value={from}
                  onChange={setFrom}
                  options={fromCandidates}
                  hiddenCount={showAllFrom ? 0 : hiddenFrom}
                  onShowHidden={() => setShowAllFrom(true)}
                />
                <TfPicker label="A" value={to} onChange={setTo} options={toCandidates} />
                <label className="text-xs text-gray-600">Pezzi
                  <input type="number" min={0} step={1} value={itemsDraft} disabled={!bothItems}
                    onChange={(e) => { setItemsDraft(e.target.value); }}
                    className="mt-0.5 w-full border border-gray-300 px-2 py-1 text-sm disabled:bg-gray-100" />
                </label>
                <label className="text-xs text-gray-600">Valore (€)
                  <input type="number" min={0} step={1} value={valueTouched ? valueDraft : String(autoValue || '')} disabled={!bothValue}
                    onChange={(e) => { setValueDraft(e.target.value); setValueTouched(true); }}
                    className="mt-0.5 w-full border border-gray-300 px-2 py-1 text-sm disabled:bg-gray-100" />
                </label>
              </div>
              {fromRow && toRow && (
                <p className="text-[11px] text-gray-500">
                  {valueTouched
                    ? 'Valore inserito a mano: andra\' confermato.'
                    : bothItems && bothValue
                      ? `${PRICE_SOURCE_LABEL[draftPrice.source]}${draftPrice.price ? ` (${fmtValue(draftPrice.price)} a pezzo)` : ''}`
                      : bothItems ? 'Senza vincolo valore su entrambi: si spostano solo pezzi.' : 'Senza vincolo pezzi su entrambi: si sposta solo valore.'}
                </p>
              )}
              <button type="button" disabled={!canAdd} onClick={addManualMove}
                className="w-full border border-gray-900 px-3 py-1.5 text-sm font-semibold text-gray-900 hover:bg-gray-50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-400">
                Aggiungi spostamento
              </button>
            </div>

            <button type="button" disabled={proposing} onClick={() => void propose()}
              className="w-full border border-[#9F6460] bg-[#9F6460]/10 px-3 py-2 text-sm font-semibold text-[#7d4f4b] hover:bg-[#9F6460]/20 disabled:opacity-50">
              {proposing ? 'Calcolo della proposta…' : proposalCount > 0 ? 'Ricalcola proposta' : 'Proponi ridistribuzione'}
            </button>
            {proposalCount > 0 && (
              <button
                type="button"
                onClick={() => {
                  setMoves((prev) => prev.filter((m) => m.origin !== 'proposta'));
                  setMessage({ tone: 'info', text: 'Proposta annullata: restano solo gli spostamenti aggiunti a mano.' });
                }}
                className="w-full border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:border-gray-900"
              >
                Annulla proposta ({proposalCount} mosse)
              </button>
            )}
            {message && (
              <p className={`text-xs ${message.tone === 'error' ? 'text-red-700' : 'text-gray-700'}`}>{message.text}</p>
            )}

            <div className="space-y-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-600">Spostamenti ({moves.length})</div>
              {moves.length === 0 && <p className="text-xs italic text-gray-500">Nessuno spostamento: aggiungine uno o chiedi una proposta.</p>}
              {moves.map((m) => (
                <div key={m.id} className={`space-y-1 border p-2 text-xs ${m.priceConfirmed ? 'border-gray-200' : 'border-amber-300 bg-amber-50'}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-gray-900">{m.from} → {m.to}</span>
                    <span className="flex items-center gap-2">
                      <span className="text-[10px] uppercase text-gray-400">{m.origin}</span>
                      <button type="button" className="text-gray-400 hover:text-gray-700" title="Togli" onClick={() => setMoves((prev) => prev.filter((x) => x.id !== m.id))}>✕</button>
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <label>Pezzi
                      <input type="number" min={0} step={1} value={m.items}
                        disabled={!has(byName.get(m.from)?.itemsBound) || !has(byName.get(m.to)?.itemsBound)}
                        onChange={(e) => {
                          const items = Math.max(0, Math.floor(Number(e.target.value) || 0));
                          updateMove(m.id, { items, ...(m.price ? { value: Math.round(items * m.price) } : {}) });
                        }}
                        className="mt-0.5 w-full border border-gray-300 px-1.5 py-0.5 disabled:bg-gray-100" />
                    </label>
                    <label>Valore (€)
                      <input type="number" min={0} step={1} value={m.value}
                        disabled={!has(byName.get(m.from)?.valueBound) || !has(byName.get(m.to)?.valueBound)}
                        onChange={(e) => updateMove(m.id, { value: Math.max(0, Number(e.target.value) || 0), priceSource: 'manca', priceConfirmed: false })}
                        className="mt-0.5 w-full border border-gray-300 px-1.5 py-0.5 disabled:bg-gray-100" />
                    </label>
                  </div>
                  <div className="flex items-center justify-between text-[11px] text-gray-500">
                    <span>{m.value > 0 && m.priceSource === 'manca' ? 'valore inserito a mano' : PRICE_SOURCE_LABEL[m.priceSource]}</span>
                    {m.priceSource !== 'vincolo' && m.value > 0 && (
                      <label className="flex items-center gap-1 font-semibold text-amber-800">
                        <input type="checkbox" checked={m.priceConfirmed} onChange={(e) => updateMove(m.id, { priceConfirmed: e.target.checked })} />
                        confermo il valore
                      </label>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-gray-200 px-5 py-3">
          <span className="text-xs text-amber-800">
            {negativeBound ? 'Uno spostamento porta un vincolo sotto zero.' : toConfirm > 0 ? `${toConfirm} spostamenti da confermare` : ''}
          </span>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:border-gray-900">Annulla</button>
            <button
              type="button"
              disabled={moves.length === 0 || toConfirm > 0 || invalidMove}
              onClick={() => onConfirm(moves)}
              className="border border-black bg-black px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400"
            >
              Conferma ({moves.length})
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
